<template>
  <div class="container mt-4">
    <div class="card">
      <div class="card-header">
        <h3>{{ isEditing ? 'Edit Specialization' : 'Add New Specialization' }}</h3>
      </div>
      <div class="card-body">
        <form @submit.prevent="submitForm">
          <div class="mb-3">
            <label for="specializationName" class="form-label">Specialization Name</label>
            <input
              type="text"
              class="form-control"
              id="specializationName"
              v-model="specialization.name"
              :class="{ 'is-invalid': errors.name }"
              required
            >
            <div class="invalid-feedback" v-if="errors.name">
              {{ errors.name }}
            </div>
            <small class="form-text text-muted">Example: Critical Care, Pediatric, Oncology</small>
          </div>

          <div class="mb-3">
            <label for="specializationCode" class="form-label">Specialization Code</label>
            <input
              type="text"
              class="form-control"
              id="specializationCode"
              v-model="specialization.code"
              :class="{ 'is-invalid': errors.code }"
              required
            >
            <div class="invalid-feedback" v-if="errors.code">
              {{ errors.code }}
            </div>
            <small class="form-text text-muted">Unique identifier code (e.g., CC, PED, ONC)</small>
          </div>

          <div class="mb-3">
            <label for="description" class="form-label">Description</label>
            <textarea
              class="form-control"
              id="description"
              rows="3"
              v-model="specialization.description"
              :class="{ 'is-invalid': errors.description }"
              required
            ></textarea>
            <div class="invalid-feedback" v-if="errors.description">
              {{ errors.description }}
            </div>
            <small class="form-text text-muted">Detailed description of this specialization and skills required</small>
          </div>

          <div class="d-flex justify-content-between">
            <button type="submit" class="btn btn-primary" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Specialization' : 'Save Specialization' }}
            </button>
            <router-link :to="{ name: 'SpecializationList' }" class="btn btn-secondary">Cancel</router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SpecializationAdd',
  data() {
    return {
      specialization: {
        name: '',
        code: '',
        description: ''
      },
      errors: {
        name: '',
        code: '',
        description: ''
      },
      isSubmitting: false,
      isEditing: false
    }
  },
  methods: {
    resetErrors() {
      this.errors = {
        name: '',
        code: '',
        description: ''
      }
    },
    async submitForm() {
      this.resetErrors()
      try {
        if (this.isSubmitting) return
        this.isSubmitting = true

        // Validate form
        if (!this.specialization.name) {
          this.errors.name = 'Specialization name is required'
          this.isSubmitting = false
          return
        }

        if (!this.specialization.code) {
          this.errors.code = 'Specialization code is required'
          this.isSubmitting = false
          return
        }

        if (!this.specialization.description) {
          this.errors.description = 'Description is required'
          this.isSubmitting = false
          return
        }

        // Submit the form
        if (this.isEditing) {
          await this.$api.put(`/specializations/${this.$route.params.id}`, this.specialization)
          this.$toast.success('Specialization updated successfully!')
        } else {
          await this.$api.post('/specializations', this.specialization)
          this.$toast.success('Specialization created successfully!')
        }
        
        // Redirect back to the list
        this.$router.push({name: 'SpecializationList'})
      } catch (error) {
        console.error(`Error ${this.isEditing ? 'updating' : 'creating'} specialization:`, error)
        
        if (error.response?.data?.errors) {
          // Handle validation errors from the API
          const apiErrors = error.response.data.errors
          
          if (apiErrors.name) this.errors.name = apiErrors.name[0]
          if (apiErrors.code) this.errors.code = apiErrors.code[0]
          if (apiErrors.description) this.errors.description = apiErrors.description[0]
        } else {
          // Generic error toast
          this.$toast.error(`Failed to ${this.isEditing ? 'update' : 'create'} specialization. Please try again.`)
        }
      } finally {
        this.isSubmitting = false
      }
    },
    async loadSpecialization() {
      try {
        const response = await this.$api.get(`/specializations/${this.$route.params.id}`)
        this.specialization = response.data.data
        this.isEditing = true
      } catch (error) {
        console.error('Error loading specialization:', error)
        this.$toast.error('Failed to load specialization details.')
        this.$router.push({name: 'SpecializationList'})
      }
    }
  },
  created() {
    if (this.$route.params.id) {
      this.loadSpecialization()
    }
  }
}
</script>

<style scoped>
.card {
  max-width: 800px;
  margin: 0 auto;
}

.form-label {
  font-weight: bold;
}

.form-text {
  font-size: 0.85rem;
}
</style>
